import type {
  PharmaCompanyChip,
  PrescriptionSortKey,
} from '@/entities/performance/api/performance.api';
import { PrescriptionLineCard } from '@/entities/performance/components/PrescriptionLineCard';
import {
  useClinicDrugs,
  useClinicPharmaCompanies,
} from '@/entities/performance/hooks/usePerformanceQueries';
import {
  groupByPrescriptionMonth,
  monthOf,
  ymLabel,
} from '@/entities/performance/lib/performance';
import {
  EmptyState,
  ErrorState,
  LoadingBlock,
} from '@/shared/components/StateViews';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Icon } from '@/shared/components/ui/icon';
import { SearchInput } from '@/shared/components/ui/input';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { cn } from '@/shared/lib/utils';
import { formatWon } from '@/shared/utils/format';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

/* ───────────── 드롭다운 (Figma 드롭다운 상태 804:4010: 기본 · 펼침 · 적용) ───────────── */

/** 글자 버튼 + ▾ — 펼치면 회색 바탕, 기본값이 아니면(적용) 파란 글자 */
const MenuTrigger = ({
  children,
  applied,
}: {
  children: ReactNode;
  applied?: boolean;
}) => (
  <DropdownMenuTrigger
    className={cn(
      'flex h-10 items-center rounded-12 px-1 text-button-14-sb outline-none transition-colors',
      'hover:bg-grey-100 focus-visible:shadow-[0_0_0_3px_var(--color-brand-200)] data-[state=open]:bg-grey-200',
      applied ? 'text-fg-accent' : 'text-fg-primary',
    )}
  >
    <span className="px-1">{children}</span>
    <Icon name="nav-arrow-down-line-24" />
  </DropdownMenuTrigger>
);

const SingleMenu = <T extends string>({
  value,
  options,
  defaultValue,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  defaultValue: T;
  onChange: (v: T) => void;
}) => (
  <DropdownMenu>
    <MenuTrigger applied={value !== defaultValue}>
      {options.find((o) => o.value === value)?.label}
    </MenuTrigger>
    <DropdownMenuContent align="end" className="w-[360px]">
      {options.map((o) => (
        <DropdownMenuItem
          key={o.value}
          selected={o.value === value}
          onSelect={() => onChange(o.value)}
        >
          {o.label}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

/* ───────────── 의약품 탭 (M-09) ───────────── */

const SORT_OPTIONS: { value: PrescriptionSortKey; label: string }[] = [
  { value: 'CONFIRMED_AMOUNT_DESC', label: '확정 금액 높은순' },
  { value: 'CONFIRMED_AMOUNT_ASC', label: '확정 금액 낮은순' },
  { value: 'ESTIMATED_AMOUNT_DESC', label: '예상 금액 높은순' },
  { value: 'ESTIMATED_AMOUNT_ASC', label: '예상 금액 낮은순' },
  { value: 'OUT_HOSPITAL_QUANTITY_DESC', label: '처방량 많은순' },
  { value: 'COMMISSION_RATE_DESC', label: '수수료율 높은순' },
];

/** 제약사 없는 정산(null)은 필터로 보내지 않는다 */
const selectableChips = (chips: PharmaCompanyChip[] = []) =>
  chips.filter(
    (c): c is PharmaCompanyChip & { pharmaCompanyId: number } =>
      c.pharmaCompanyId !== null,
  );

/**
 * M-09 의약품 탭 (Figma 712:3430) — 의약품 검색 · 정렬 · 제약사(여러 개)는 서버
 * 카드는 처방월로 묶고 최근 처방월이 위다. 묶음 합계를 맞추려고 남은 페이지를 모두 불러온다
 * (병의원 한 곳 · 한 달 단위라 양이 많지 않다).
 * 기획 변경: 제약사 필터의 [초기화]와 선택 수 표시(제약사(2))는 없앴다.
 */
export const DrugsTab = ({
  ym,
  clinicId,
  clinicName,
}: {
  ym: string;
  clinicId: number;
  clinicName?: string;
}) => {
  const [keyword, setKeyword] = useState('');
  const drugName = useDebouncedValue(keyword.trim());
  const [sortKey, setSortKey] = useState<PrescriptionSortKey>(
    'CONFIRMED_AMOUNT_DESC',
  );
  const [pharmaIds, setPharmaIds] = useState<number[]>([]);

  const pharmas = useClinicPharmaCompanies(ym, clinicId);
  const drugs = useClinicDrugs(ym, clinicId, {
    drugName: drugName || undefined,
    sortKeys: [sortKey],
    pharmaCompanyIds: pharmaIds.length ? pharmaIds : undefined,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = drugs;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const lines = useMemo(
    () => drugs.data?.pages.flatMap((p) => p.content) ?? [],
    [drugs.data],
  );
  const groups = useMemo(() => groupByPrescriptionMonth(lines), [lines]);
  const total = drugs.data?.pages[0]?.totalCount ?? 0;
  const filtered = !!drugName || pharmaIds.length > 0;
  const range =
    groups.length > 1
      ? `${monthOf(groups[groups.length - 1].ym)}~${monthOf(groups[0].ym)}월`
      : groups.length === 1
        ? `${monthOf(groups[0].ym)}월`
        : null;
  const chips = selectableChips(pharmas.data?.pharmaCompanies);

  const togglePharma = (id: number) =>
    setPharmaIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    );

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2 px-6">
        <p className="flex items-center gap-2 text-subtitle-18-b">
          <span className="text-fg-primary">{ymLabel(ym)} 정산</span>
          <span className="text-fg-accent">{total}건</span>
        </p>
        {range && !filtered && (
          <p className="text-body-14-r text-fg-tertiary">
            {monthOf(ym)}월 정산에는 {range} 처방이 함께 들어 있어요.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-3 px-6 sm:flex-row sm:items-center sm:justify-between">
        <SearchInput
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="의약품 검색"
          aria-label="의약품 검색"
          className="w-full sm:w-80"
        />
        <div className="flex items-center gap-2 self-end">
          <SingleMenu
            value={sortKey}
            options={SORT_OPTIONS}
            defaultValue="CONFIRMED_AMOUNT_DESC"
            onChange={setSortKey}
          />
          <DropdownMenu>
            <MenuTrigger applied={pharmaIds.length > 0}>제약사</MenuTrigger>
            <DropdownMenuContent
              align="end"
              className="max-h-[304px] w-[360px] overflow-y-auto"
            >
              {chips.length === 0 && (
                <DropdownMenuItem disabled iconSlot={false}>
                  제약사가 없어요
                </DropdownMenuItem>
              )}
              {chips.map((c) => (
                <DropdownMenuItem
                  key={c.pharmaCompanyId}
                  selected={pharmaIds.includes(c.pharmaCompanyId)}
                  // 여러 개를 고를 수 있게 고른 뒤에도 닫지 않는다
                  onSelect={(e) => {
                    e.preventDefault();
                    togglePharma(c.pharmaCompanyId);
                  }}
                >
                  {c.pharmaCompanyName}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {drugs.isPending ? (
        <div className="px-6">
          <LoadingBlock className="h-[320px]" />
        </div>
      ) : drugs.isError ? (
        <ErrorState onRetry={() => void drugs.refetch()} />
      ) : lines.length === 0 ? (
        <EmptyState
          className="py-14"
          message={
            filtered
              ? '조건에 맞는 처방 내역이 없어요.'
              : '이 달에 확인된 처방 내역이 없어요.'
          }
        />
      ) : (
        <div className="flex flex-col gap-6">
          {groups.map((g) => (
            <section key={g.ym} className="flex flex-col">
              <div className="flex flex-wrap items-center gap-4 px-6 text-body-16-r">
                <p className="flex items-center gap-2 text-subtitle-16-b">
                  <span className="text-fg-primary">
                    {monthOf(g.ym)}월 처방
                  </span>
                  <span className="text-fg-accent">{g.lines.length}건</span>
                </p>
                <div className="flex flex-1 items-center justify-end gap-6 text-right">
                  <p className="flex items-center gap-1">
                    <span className="text-body-16-m text-fg-tertiary">
                      예상
                    </span>
                    <span className="text-fg-primary">
                      {formatWon(g.estimated)}
                    </span>
                  </p>
                  <p className="flex items-center gap-1">
                    <span className="text-body-16-m text-fg-tertiary">
                      확정
                    </span>
                    <span className="text-body-16-sb text-fg-primary">
                      {formatWon(g.confirmed)}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2 px-6 py-4">
                {g.lines.map((line) => (
                  <PrescriptionLineCard
                    key={line.prescriptionId}
                    line={line}
                    clinicName={clinicName}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

/* ───────────── 제약사 탭 (M-17) ───────────── */

type PharmaSort =
  | 'CONFIRMED_DESC'
  | 'CONFIRMED_ASC'
  | 'ESTIMATED_DESC'
  | 'ESTIMATED_ASC'
  | 'DRUGS_DESC';

const PHARMA_SORT_OPTIONS: { value: PharmaSort; label: string }[] = [
  { value: 'CONFIRMED_DESC', label: '확정 금액 높은순' },
  { value: 'CONFIRMED_ASC', label: '확정 금액 낮은순' },
  { value: 'ESTIMATED_DESC', label: '예상 금액 높은순' },
  { value: 'ESTIMATED_ASC', label: '예상 금액 낮은순' },
  { value: 'DRUGS_DESC', label: '품목 많은순' },
];

const PHARMA_SORTERS: Record<
  PharmaSort,
  (a: PharmaCompanyChip, b: PharmaCompanyChip) => number
> = {
  CONFIRMED_DESC: (a, b) => b.confirmedAmount - a.confirmedAmount,
  CONFIRMED_ASC: (a, b) => a.confirmedAmount - b.confirmedAmount,
  ESTIMATED_DESC: (a, b) => b.estimatedAmount - a.estimatedAmount,
  ESTIMATED_ASC: (a, b) => a.estimatedAmount - b.estimatedAmount,
  DRUGS_DESC: (a, b) => b.drugCount - a.drugCount,
};

/** 펼친 제약사의 의약품 — 처방월 최신이 위, 행마다 "n월 처방" */
const PharmaDrugs = ({
  ym,
  clinicId,
  clinicName,
  pharmaCompanyId,
}: {
  ym: string;
  clinicId: number;
  clinicName?: string;
  pharmaCompanyId: number | null;
}) => {
  const drugs = useClinicDrugs(
    ym,
    clinicId,
    {
      pharmaCompanyIds:
        pharmaCompanyId === null ? undefined : [pharmaCompanyId],
      sortKeys: ['CONFIRMED_AMOUNT_DESC'],
    },
    { enabled: pharmaCompanyId !== null },
  );
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = drugs;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const lines = groupByPrescriptionMonth(
    drugs.data?.pages.flatMap((p) => p.content) ?? [],
  ).flatMap((g) => g.lines);

  const rowClass = 'border-b border-line-grey bg-brand-75 p-4';
  if (pharmaCompanyId === null)
    return (
      <p className={cn(rowClass, 'text-body-14-r text-fg-tertiary')}>
        제약사 정보 없이 정산만 들어온 금액이에요.
      </p>
    );
  if (drugs.isPending)
    return (
      <div className={rowClass}>
        <LoadingBlock className="h-[120px]" />
      </div>
    );
  if (drugs.isError)
    return (
      <div className={rowClass}>
        <ErrorState onRetry={() => void drugs.refetch()} />
      </div>
    );
  if (lines.length === 0)
    return (
      <p className={cn(rowClass, 'text-body-14-r text-fg-tertiary')}>
        이 제약사는 정산만 있고 처방 카드가 없어요.
      </p>
    );
  return (
    <>
      {lines.map((line) => (
        <div key={line.prescriptionId} className={rowClass}>
          <PrescriptionLineCard
            line={line}
            clinicName={clinicName}
            variant="plain"
            aside={
              <p className="shrink-0 text-right text-body-16-sb whitespace-nowrap text-fg-tertiary">
                {monthOf(line.prescriptionYearMonth)}월 처방
              </p>
            }
          />
        </div>
      ))}
    </>
  );
};

/**
 * M-17 제약사 탭 (Figma 789:10041 · 804:3049)
 * 기획 변경: 칩 단일 선택 → 제약사 표에서 행을 펼쳐 보기, 여러 제약사를 동시에 펼칠 수 있다.
 * 합계는 제약사 API 값(카드 합이 아님). 처음에는 1위 제약사를 펼쳐 둔다.
 */
export const PharmaTab = ({
  ym,
  clinicId,
  clinicName,
}: {
  ym: string;
  clinicId: number;
  clinicName?: string;
}) => {
  const pharmas = useClinicPharmaCompanies(ym, clinicId);
  const [sort, setSort] = useState<PharmaSort>('CONFIRMED_DESC');
  const [opened, setOpened] = useState<Set<number | null> | null>(null);

  const rows = useMemo(
    () => [...(pharmas.data?.pharmaCompanies ?? [])].sort(PHARMA_SORTERS[sort]),
    [pharmas.data, sort],
  );
  // 처음에는 서버 1위(확정↓ → 예상↓) 제약사를 펼친다
  const openSet =
    opened ??
    new Set(
      pharmas.data?.pharmaCompanies[0]
        ? [pharmas.data.pharmaCompanies[0].pharmaCompanyId]
        : [],
    );
  const toggle = (id: number | null) => {
    const next = new Set(openSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpened(next);
  };

  return (
    <div className="flex flex-col gap-8 px-6">
      <div className="flex items-center justify-between gap-4">
        <p className="flex items-center gap-2 text-subtitle-18-b">
          <span className="text-fg-secondary">제약사</span>
          {pharmas.data && (
            <span className="text-fg-accent">
              {rows.length}곳 · {pharmas.data.drugCount}품목
            </span>
          )}
        </p>
        <SingleMenu
          value={sort}
          options={PHARMA_SORT_OPTIONS}
          defaultValue="CONFIRMED_DESC"
          onChange={setSort}
        />
      </div>

      {pharmas.isPending ? (
        <LoadingBlock className="h-[320px]" />
      ) : pharmas.isError ? (
        <ErrorState onRetry={() => void pharmas.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          className="py-14"
          message="이 달에 확인된 제약사 실적이 없어요."
        />
      ) : (
        <div className="flex flex-col overflow-hidden rounded-12 border border-line-grey">
          <div className="flex h-[65px] items-center gap-4 bg-surface-grey p-4 text-body-15-sb text-fg-tertiary">
            <p className="w-[180px] shrink-0 lg:w-[280px]">제약사</p>
            <div className="flex min-w-0 flex-1 items-center gap-6 text-right">
              <p className="min-w-0 flex-1">품목</p>
              <p className="min-w-0 flex-1">예상 금액</p>
              <p className="min-w-0 flex-1">확정 금액</p>
              <span className="size-8 shrink-0" />
            </div>
          </div>
          {rows.map((c) => {
            const open = openSet.has(c.pharmaCompanyId);
            return (
              <div key={c.pharmaCompanyId ?? 'none'} className="flex flex-col">
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => toggle(c.pharmaCompanyId)}
                  className="flex items-center gap-4 p-4 text-left outline-none transition-colors hover:bg-surface-grey focus-visible:bg-surface-grey"
                >
                  <p className="w-[180px] shrink-0 truncate text-subtitle-16-b text-fg-primary lg:w-[280px]">
                    {c.pharmaCompanyName ?? '제약사 없음'}
                  </p>
                  <div className="flex min-w-0 flex-1 items-center gap-6 text-right text-body-16-r text-fg-primary">
                    <p className="min-w-0 flex-1">{c.drugCount}</p>
                    <p className="min-w-0 flex-1">
                      {formatWon(c.estimatedAmount)}
                    </p>
                    <p className="min-w-0 flex-1 text-body-16-sb">
                      {formatWon(c.confirmedAmount)}
                    </p>
                    <span className="flex size-8 shrink-0 items-center justify-center text-fg-tertiary">
                      <Icon
                        name={
                          open
                            ? 'nav-arrow-up-line-16'
                            : 'nav-arrow-down-line-16'
                        }
                      />
                    </span>
                  </div>
                </button>
                {open && (
                  <PharmaDrugs
                    ym={ym}
                    clinicId={clinicId}
                    clinicName={clinicName}
                    pharmaCompanyId={c.pharmaCompanyId}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
